import { CloseSquare } from "iconsax-react";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import instance from "../../../../../../axios/instance";

export const SubscribePatientModal = ({
  isOpen,
  onClose,
  onSuccess,
  isLoading: externalLoading,
}) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    productId: "",
    treatmentId: "",
    medication: "",
  });

  const [products, setProducts] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [filteredTreatments, setFilteredTreatments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const token = window.sessionStorage.getItem("ACCESS_TOKEN");

  useEffect(() => {
    if (isOpen) {
      fetchProducts();
      fetchTreatments();
    }
  }, [isOpen]);

  useEffect(() => {
    if (formData.productId && Array.isArray(treatments)) {
      const filtered = treatments.filter((t) => {
        return t.product_id == formData.productId;
      });
      setFilteredTreatments(filtered);
      if (!filtered.find((t) => t.id === formData.treatmentId)) {
        setFormData((prev) => ({ ...prev, treatmentId: "" }));
      }
    } else {
      setFilteredTreatments([]);
    }
  }, [formData.productId, treatments]);

  const fetchProducts = async () => {
    try {
      const response = await instance.get("/doctor/cms/products", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(response.data || []);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
      setProducts([]);
    }
  };

  const fetchTreatments = async () => {
    try {
      const response = await instance.get("/doctor/cms/treatments", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const treatmentsData = response.data.data || response.data || [];
      setTreatments(Array.isArray(treatmentsData) ? treatmentsData : []);
    } catch (error) {
      console.error("Error fetching treatments:", error);
      toast.error("Failed to load treatments");
      setTreatments([]);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!formData.productId) newErrors.productId = "Product is required";
    if (!formData.treatmentId) newErrors.treatmentId = "Treatment is required";
    if (!formData.medication.trim())
      newErrors.medication = "Medication (ml) is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await instance.post("/doctor/prescriptions/subscribe", formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Patient subscribed successfully!");
      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error("Error subscribing patient:", error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || "Failed to subscribe patient. Please check your connection and try again.";
      toast.error(errorMessage, {
        toastId: "subscribe-patient-error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      productId: "",
      treatmentId: "",
      medication: "",
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  const loading = isLoading || externalLoading;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={handleClose}
      ></div>

      <div className="fixed w-[90%] max-w-[600px] top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 z-50 bg-white rounded-md shadow-lg max-h-[90vh] overflow-y-auto">
        <div className="px-5 pt-4 flex justify-between items-center rounded-t-3xl sticky top-0 bg-white border-b pb-4">
          <div className="text-[15px] font-medium select-none">
            <h2>Subscribe Patient</h2>
          </div>
          <button onClick={handleClose} disabled={loading}>
            <CloseSquare variant="Bold" size={24} />
          </button>
        </div>

        <div className="px-5 py-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[14px] font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleChange("firstName", e.target.value)}
                placeholder="Enter first name"
                className={`w-full px-4 py-2 border ${errors.firstName ? "border-red-500" : "border-gray-300"} rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                disabled={loading}
              />
              {errors.firstName && (
                <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
              )}
            </div>

            <div>
              <label className="block text-[14px] font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleChange("lastName", e.target.value)}
                placeholder="Enter last name"
                className={`w-full px-4 py-2 border ${errors.lastName ? "border-red-500" : "border-gray-300"} rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                disabled={loading}
              />
              {errors.lastName && (
                <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-[14px] font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              placeholder="Enter email address"
              className={`w-full px-4 py-2 border ${errors.email ? "border-red-500" : "border-gray-300"} rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
              disabled={loading}
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-[14px] font-medium text-gray-700 mb-1">
              Product <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.productId}
              onChange={(e) => handleChange("productId", e.target.value)}
              className={`w-full px-4 py-2 border ${errors.productId ? "border-red-500" : "border-gray-300"} rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
              disabled={loading}
            >
              <option value="">Select a product</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
            {errors.productId && (
              <p className="text-red-500 text-xs mt-1">{errors.productId}</p>
            )}
          </div>

          <div>
            <label className="block text-[14px] font-medium text-gray-700 mb-1">
              Treatment <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.treatmentId}
              onChange={(e) => handleChange("treatmentId", e.target.value)}
              className={`w-full px-4 py-2 border ${errors.treatmentId ? "border-red-500" : "border-gray-300"} rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
              disabled={loading || !formData.productId}
            >
              <option value="">
                {formData.productId
                  ? "Select a treatment"
                  : "Select a product first"}
              </option>
              {filteredTreatments.map((treatment) => (
                <option key={treatment.id} value={treatment.id}>
                  {treatment.name} - ${treatment.price}
                </option>
              ))}
            </select>
            {errors.treatmentId && (
              <p className="text-red-500 text-xs mt-1">{errors.treatmentId}</p>
            )}
          </div>

          <div>
            <label className="block text-[14px] font-medium text-gray-700 mb-1">
              Medication (ml) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.medication}
              onChange={(e) => handleChange("medication", e.target.value)}
              placeholder="e.g., 2.5ml"
              className={`w-full px-4 py-2 border ${errors.medication ? "border-red-500" : "border-gray-300"} rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
              disabled={loading}
            />
            {errors.medication && (
              <p className="text-red-500 text-xs mt-1">{errors.medication}</p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 px-5 pb-4 rounded-b-3xl border-t pt-4 sticky bottom-0 bg-white">
          <button
            className="px-4 py-2 text-[14px] font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 text-[14px] font-medium text-white bg-black rounded-full hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            Subscribe Patient
          </button>
        </div>
      </div>
    </>
  );
};
